import { businessDayBounds } from "@/features/appointments/utils/bookingTime";
import { validDateKey } from "@/shared/utils/calendarDate";
import { getBusinessDateKey } from "@/shared/utils/formatters";

export function eventLocalValue(date: Date, timezone: string) {
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(date);
  return `${getBusinessDateKey(date, timezone)}T${time}`;
}

export function zonedLocalToDate(value: string, timezone: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) || !validDateKey(value.slice(0, 10))) {
    throw new Error("Enter a valid date and time.");
  }
  const approximate = new Date(`${value}:00Z`).getTime();
  if (!Number.isFinite(approximate)) throw new Error("Enter a valid date and time.");
  const matches: number[] = [];
  // Sample offsets on both sides of a transition, then round-trip each candidate.
  for (let hours = -36; hours <= 36; hours += 6) {
    const sample = new Date(approximate + hours * 3_600_000);
    const offset = new Date(`${eventLocalValue(sample, timezone)}:00Z`).getTime() - sample.getTime();
    const candidate = approximate - offset;
    if (eventLocalValue(new Date(candidate), timezone) === value) matches.push(candidate);
  }
  if (!matches.length) throw new Error("This local time does not exist because the clocks change. Choose another time.");
  return new Date(Math.min(...matches));
}

export function eventRange(
  start: string, end: string, isAllDay: boolean, timezone: string,
  original?: { startAt: Date; endAt: Date; isAllDay: boolean },
  minimumStartDay?: string,
) {
  let startAt: Date;
  let endAt: Date;
  if (isAllDay) {
    if (!validDateKey(start) || !validDateKey(end)) throw new Error("Choose a valid start and end date.");
    startAt = businessDayBounds(start, timezone).startAt;
    const lastDay = businessDayBounds(end, timezone);
    endAt = lastDay.endAt;
    if (getBusinessDateKey(startAt, timezone) !== start || getBusinessDateKey(lastDay.startAt, timezone) !== end || end < start) {
      throw new Error("Choose a valid date range.");
    }
  } else {
    // Resolve unchanged endpoints before ordering: clock labels can run backwards across a DST fold.
    startAt = original && !original.isAllDay && start === eventLocalValue(original.startAt, timezone)
      ? original.startAt : zonedLocalToDate(start, timezone);
    endAt = original && !original.isAllDay && end === eventLocalValue(original.endAt, timezone)
      ? original.endAt : zonedLocalToDate(end, timezone);
  }
  if (minimumStartDay && getBusinessDateKey(startAt, timezone) < minimumStartDay) {
    throw new Error("Start date must be today or later.");
  }
  if (endAt <= startAt) throw new Error("End must be after start.");
  return { startAt, endAt };
}
